import { Check, X } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { DomainEventUpdateItemDetailed } from '@/services/api/generated/matterCmsUpdatesService';
import type { DomainEventType } from '@/services/api/matterChangesService';
import { matterCmsUpdatesApiClient } from '@/services/api/matterCmsUpdatesService';

import { Button } from '../ui/catalyst/button';
import { EventRenderer } from './events/EventRenderer';

interface UpdateRendererProps {
  update: DomainEventUpdateItemDetailed;
}

interface PendingUpdateRendererProps {
  update: DomainEventUpdateItemDetailed;
}

interface CompletedUpdateRendererProps {
  update: DomainEventUpdateItemDetailed;
}

const PendingUpdateRenderer = ({ update }: PendingUpdateRendererProps) => {
  const [markTransferred] =
    matterCmsUpdatesApiClient.useCmsUpdatesMarkTransferredMutation();
  const [markSkipped] =
    matterCmsUpdatesApiClient.useCmsUpdatesMarkSkippedMutation();

  const handleTransferred = async () => {
    try {
      await markTransferred({
        matterId: update.event.matter_id,
        id: update.id,
      }).unwrap();
      toast.success('Update marked as transferred');
    } catch (error) {
      toast.error('Failed to mark as transferred');
    }
  };

  const handleSkip = async () => {
    try {
      await markSkipped({
        matterId: update.event.matter_id,
        id: update.id,
      }).unwrap();
      toast.success('Update marked as skipped');
    } catch (error) {
      toast.error('Failed to mark as skipped');
    }
  };

  return (
    <div>
      <EventRenderer event={update.event as unknown as DomainEventType} />
      <div className="flex ml-16 gap-2 mt-3 pb-4 border-b border-gray-200">
        <Button color="green" onClick={handleTransferred}>
          <Check data-slot="icon" />
          Transferred
        </Button>
        <Button outline onClick={handleSkip}>
          <X data-slot="icon" />
          Skip
        </Button>
      </div>
    </div>
  );
};

const CompletedUpdateRenderer = ({ update }: CompletedUpdateRendererProps) => {
  const isSkipped = update.status === 'skipped';

  return (
    <div className="relative">
      {/* Status indicator badge */}
      <div
        className={cn(
          'absolute -left-2 top-1/2 -translate-y-1/2 px-2 py-1 rounded text-xs font-medium',
          isSkipped
            ? 'bg-gray-200 text-gray-700'
            : update.status === 'filtered'
              ? 'bg-gray-200 text-gray-700'
              : 'bg-green-200 text-green-800',
        )}
      >
        {isSkipped
          ? 'Skipped'
          : update.status === 'filtered'
            ? 'Filtered'
            : 'Transferred'}
      </div>

      {/* Event content with reduced opacity */}
      <div className="opacity-60 ml-24">
        <EventRenderer event={update.event as unknown as DomainEventType} />
      </div>
    </div>
  );
};

export const UpdateRenderer = ({ update }: UpdateRendererProps) => {
  const isPending = update.status === 'pending';

  if (isPending) {
    return <PendingUpdateRenderer update={update} />;
  }

  return <CompletedUpdateRenderer update={update} />;
};
